import * as React from "react";
import classnames from "classnames";

import { SEO_URLS } from "./landing_utils";

import css from "./shared.module.scss";

import FILE_SEARCH from "../../../assets/images/icons/file_search_gradient.svg";
import FOLDER_OPEN from "../../../assets/images/icons/folder_open_gradient.svg";
import ID_CARD from "../../../assets/images/icons/id_card_gradient.svg";
import USERGROUP_ADD from "../../../assets/images/icons/usergroup_add_gradient.svg";
import LogoURL from "../../../assets/images/stotles_logo_blue.svg";

type DataType = "buyer" | "record";

export const signupLink = (appRootUrl: string, dataType?: DataType, id?: string) => {
  return (
    appRootUrl +
    SEO_URLS.signUp +
    "?utm_source=seo_lp" +
    (dataType ? "&dataType=" + dataType : "") +
    (id ? "&id=" + id : "")
  );
};
export const signinLink = (appRootUrl: string, dataType?: DataType, id?: string) => {
  return (
    appRootUrl +
    SEO_URLS.signIn +
    "?utm_source=seo_lp" +
    (dataType ? "&dataType=" + dataType : "") +
    (id ? "&id=" + id : "")
  );
};

export function Footer(): JSX.Element {
  return (
    <footer>
      <p>© {new Date().getFullYear()} Stotle Ltd.</p>
      <a href="https://www.notion.so/stotles/Stotles-Privacy-Policy-4a597d71636d443c9be325fef2382701">
        Privacy
      </a>
      <a href="https://www.notion.so/stotles/Stotles-Cookie-Policy-e0a46652fe9f4115a9d4e90d3c5c274f">
        Cookies
      </a>
      <a href="mailto:team@stotles.com">team@stotles.com</a>
    </footer>
  );
}

export function Header({
  appRootUrl,
  dataType,
  id,
}: {
  appRootUrl: string;
  dataType?: DataType;
  id?: string;
}): JSX.Element {
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(function mount() {
    if (typeof window !== "undefined") {
      const cb = () => {
        if (window?.scrollY > 0) setScrolled(true);
        else setScrolled(false);
      };
      window.addEventListener("scroll", cb);
      return function unMount() {
        window.removeEventListener("scroll", cb);
      };
    }
  });

  return (
    <header className={classnames({ [css.scrolled]: scrolled }, css.header)}>
      <div>
        <a href="https://www.stotles.com" data-id="explore_articles">
          <img className={css.topnavLogo} src={LogoURL} alt="Stotles logo" />
        </a>
        <div className={css.navSignUp}>
          <a
            target="_self"
            href={signinLink(appRootUrl, dataType, id)}
            className={css.headerSignInButton}
            data-id="login_cta"
          >
            Log in
          </a>
          <a
            target="_self"
            href={signupLink(appRootUrl, dataType, id)}
            className={css.signUpButton}
            data-id="signup_cta"
          >
            Sign up free
          </a>
        </div>
      </div>
    </header>
  );
}

export function FooterSignUpSection({
  appRootUrl,
  dataType,
  id,
  className,
}: {
  appRootUrl: string;
  dataType?: DataType;
  id?: string;
  className?: string;
}): JSX.Element {
  return (
    <section className={classnames(css.footerSignUp, className)}>
      <h3 className={css.signUpHeading}>Stop chasing tenders, start getting ahead.</h3>
      <a
        target="_self"
        href={signupLink(appRootUrl, dataType, id)}
        className={classnames(css.ctaButton, css.blueButton)}
        data-id="signup_cta"
      >
        Create your free feed
      </a>
      <p className={css.signInLink}>
        Already have an account?{" "}
        <a target="_self" href={signinLink(appRootUrl, dataType, id)} data-id="login_cta">
          Sign in
        </a>
      </p>
    </section>
  );
}

export function usePageViewLogger(): void {
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      const amplitudeInstance = window.amplitude;
      if (amplitudeInstance) {
        amplitudeInstance.track(`Page View: Brochure site`, {
          URL: `${window.location.pathname}`,
        });
      }
    }
  }, []);
}

const PROMO_FEATURES = [
  {
    name: "Tender tracking",
    description:
      "Access a feed of government opportunities tailored to you, in one view. Receive email alerts and integrate with your CRM to stay up-to-date.",
    icon: FILE_SEARCH,
    alt: "Looking glass on top of a file icon",
  },
  {
    name: "Proactive prospecting",
    description: `Get ahead of competitors by reaching out to key decision-makers within buying organisations directly.`,
    icon: ID_CARD,
    alt: "ID card icon",
  },
  {
    name: "360° account briefings",
    description:
      "Create in-depth briefings on buyer organisations based on their historical & upcoming procurement activity.",
    icon: FOLDER_OPEN,
    alt: "Open folder icon",
  },
  {
    name: "Collaboration tools",
    description:
      "Streamline sales workflows with team collaboration and communication features, and integrate with your favourite sales tools.",
    icon: USERGROUP_ADD,
    alt: "Teamwork icon",
  },
];

export function PromoFeaturesSection({ className }: { className?: string }): JSX.Element {
  return (
    <section className={classnames(css.blueBox, className)}>
      <ul className={css.featureContainer}>
        {PROMO_FEATURES.map((f) => (
          <li key={f.name}>
            <h3>
              <img className={css.featureIcon} height={24} width={24} src={f.icon} alt={f.alt} />
              <span>{f.name}</span>
            </h3>
            <p className={css.desc}>{f.description}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 *
 * Promo section asking the user to signup. Displays text and button in a horizontal alignment
 * @param param0
 * @returns
 */
type PromoSectionProps = {
  text: React.ReactNode;
  appRootUrl: string;
  dataType?: DataType;
  id?: string;
  large?: boolean;
  className?: string;
};
export function PromoSection({
  text,
  appRootUrl,
  dataType,
  id,
  large,
  className,
}: PromoSectionProps): JSX.Element {
  return (
    <div className={classnames(css.navyBoxHorizontal, className)}>
      <h3 className={large ? css.largePromoText : css.promoText}>{text}</h3>
      <a
        href={signupLink(appRootUrl, dataType, id)}
        data-id="signup_cta"
        className={classnames(
          css.blueButton,
          css.button,
          large ? css.largePromoButton : css.promoButton,
        )}
      >
        {large ? "Sign up free" : "Start for free"}
      </a>
    </div>
  );
}
